import React, { useEffect, useRef, useState } from "react";
import { DOT, gbp } from "@/lib/stages";
import { ST_CLASS, StagePill, StageRing, ringColor } from "./StagePill";
import { toast } from "sonner";
import { I } from "@/components/portal/sys/icons";
import { api } from "@/lib/api";

/* The portal's parts, each one the element the v3 mockups draw. Nothing here invents a
   style: it picks a class out of the design system (`styles/portal-sys.css`, generated from
   `_mockups/v3/sys.css`) so a page reads the same in React as it does in the mockup. */

/* ---------- type and layout ---------- */

export const PageHead = ({ eyebrow, title, lede, actions, meta, testId }) => (
  <div className="head" data-testid={testId}>
    <div className="head-row">
      <div style={{ minWidth: 0 }}>
        {eyebrow && <div className="eyebrow">{eyebrow}</div>}
        <h1 className="t">{title}</h1>
      </div>
      {actions && <div className="head-actions">{actions}</div>}
    </div>
    {lede && <p className="lede">{lede}</p>}
    {meta && (
      <div className="head-meta" style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 18, flexWrap: "wrap" }}>{meta}</div>
    )}
  </div>
);

export const Sec = ({ title, sub, right, children, style }) => (
  <div className="sec" style={style}>
    {(title || right) && (
      <div className="sec-head">
        <div>
          {title && <h2 className="s">{title}</h2>}
          {sub && <p className="sec-sub">{sub}</p>}
        </div>
        {right}
      </div>
    )}
    {children}
  </div>
);

export const Divider = ({ style }) => <div className="divider" style={style} />;

/**
 * `.page-wide` — the main column and the sticky 320px rail, exactly as the mockups lay it
 * out. The page head sits above this, full width, so a heading is never squeezed by the rail.
 */
export const PageBody = ({ rail, children }) => (
  rail ? (
    <div className="page-wide">
      <div style={{ minWidth: 0 }}>{children}</div>
      <aside className="rail" data-testid="portal-rail">{rail}</aside>
    </div>
  ) : <>{children}</>
);

/* ---------- the headline numbers ---------- */

/** A figure like "£11,563" prints its currency mark small and muted, as the mockup does. */
const Figure = ({ v }) => {
  const s = String(v ?? "");
  if (s.startsWith("£")) return <><span className="cur">£</span>{s.slice(1)}</>;
  return <>{s}</>;
};

export const StatStrip = ({ items, testId }) => (
  <div className={`stats${items.length === 3 ? " stats-3" : items.length === 5 ? " stats-5" : ""}`} data-testid={testId}>
    {items.map((s) => (
      <div className="stat" key={s.k} data-testid={s.testId}>
        <div className="k">{s.k}</div>
        <div className="v" style={s.tone === "green" ? { color: "var(--green)" } : s.tone === "red" ? { color: "var(--red)" } : undefined}>
          <Figure v={s.v} />
          {s.unit && <span style={{ fontSize: 17, fontWeight: 700, color: "var(--mut-2)" }}> {s.unit}</span>}
        </div>
        {s.h && <div className="h">{s.h}</div>}
      </div>
    ))}
  </div>
);

/* ---------- cards ---------- */

export const Panel = ({ title, sub, right, children, footer, pad = true, testId, style }) => (
  <section className="card" data-testid={testId} style={style}>
    {(title || right) && (
      <div className="card-h">
        <div style={{ minWidth: 0 }}>
          {title && <div className="card-t">{title}</div>}
          {sub && <div className="sec-sub" style={{ marginTop: 4 }}>{sub}</div>}
        </div>
        {right}
      </div>
    )}
    {pad ? <div className="card-b">{children}</div> : children}
    {footer && <div className="card-f">{footer}</div>}
  </section>
);

export const RailCard = ({ title, children, testId, style }) => (
  <div className="rail-card" data-testid={testId} style={style}>
    {title && <div className="rail-t">{title}</div>}
    {children}
  </div>
);

/* ---------- the navy desk card ---------- */

export const Mgr = ({ label = "Case support", who = "Your Connexis desk", role, children, live, testId }) => (
  <div className="mgr" data-testid={testId}>
    <div className="lab">{label}</div>
    <div className="who">{who}</div>
    {role && <div className="role">{role}</div>}
    {children && <div style={{ display: "flex", flexDirection: "column", gap: 9, marginTop: 19 }}>{children}</div>}
    {live && <div className="live"><i />{live}</div>}
  </div>
);

/**
 * DeskSupport — the named human a partner should actually contact.
 *
 * This one card sits on sixteen pages across both portals and used to say the same generic thing
 * on every one of them: "Your Connexis desk". A partner Richard recruited has a relationship with
 * Richard, and Richard earns an override on their deals, so he is both the person they would
 * rather ring and the person with a reason to answer. `/api/portal/my-contact` resolves that from
 * `referred_by`, falling back to the house manager for anyone Connexis signed up directly.
 *
 * Fetched once per page load and shared, because sixteen copies of a card must not mean sixteen
 * requests.
 */
let contactCache = null;

const useMyContact = () => {
  const [contact, setContact] = React.useState(() => contactCache?.value || null);
  React.useEffect(() => {
    if (contactCache?.value) { setContact(contactCache.value); return; }
    if (!contactCache) {
      contactCache = {
        promise: api.get("/portal/my-contact")
          .then((r) => { contactCache.value = r.data; return r.data; })
          .catch(() => { contactCache = null; return null; }),
      };
    }
    let alive = true;
    contactCache.promise?.then((v) => { if (alive && v) setContact(v); });
    return () => { alive = false; };
  }, []);
  return contact;
};

const waLink = (digits, text) => `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;

export const DeskSupport = ({ title, body, testId = "portal-desk-support", live, chat = true }) => {
  const c = useMyContact();
  const who = title || c?.name || "Your Connexis desk";
  const recruiter = c?.kind === "recruiter";
  const desk = recruiter ? c?.desk : null;            // the named Connexis route behind a recruiter
  const sub = c?.named ? [c.relationship, c.company].filter(Boolean).join(" · ") : null;
  // A recruiter is not a desk and cannot promise desk hours, so the live line adapts.
  const liveLine = live || (recruiter ? "Usually quickest on WhatsApp" : "Desk open, replies in minutes");

  return (
    <Mgr label={recruiter ? "Your contact" : "Case support"} who={who} role={body}
         live={liveLine} testId={testId}>
      {sub && (
        <div style={{ marginTop: -6, marginBottom: 3, fontSize: 11.5, fontWeight: 600,
          letterSpacing: ".02em", color: "rgba(255,255,255,.55)" }} data-testid={`${testId}-relationship`}>
          {sub}
        </div>
      )}
      {c?.whatsapp && (
        <a className="btn btn-primary btn-block" target="_blank" rel="noreferrer"
           href={waLink(c.whatsapp, c.greet ? `Hi ${c.greet} — ` : "Hi — ")}
           data-testid={`${testId}-whatsapp`}>
          WhatsApp{c.greet ? ` ${c.greet}` : ""}
        </a>
      )}
      {chat && <button className={`btn btn-block ${c?.whatsapp ? "btn-ghost" : "btn-primary"}`}
              onClick={() => window.dispatchEvent(new CustomEvent("portal-chat:open"))}
              data-testid={`${testId}-message`}
              style={c?.whatsapp ? { background: "rgba(255,255,255,.07)", borderColor: "rgba(255,255,255,.18)", color: "#fff" } : undefined}>
        {/* A recruiter cannot check a payout or chase a desk, so the second route must obviously
            be Connexis — and it names the manager who actually owns the account. */}
        {desk?.name ? `Message ${desk.greet || desk.name} at Connexis` : recruiter ? "Message the Connexis desk" : "Message the desk"}
      </button>}
      {desk?.whatsapp && (
        <a target="_blank" rel="noreferrer" href={waLink(desk.whatsapp, desk.greet ? `Hi ${desk.greet} — ` : "Hi — ")}
           data-testid={`${testId}-desk-whatsapp`}
           style={{ display: "block", marginTop: 9, fontSize: 11.5, fontWeight: 700, textAlign: "center",
             color: "rgba(255,255,255,.62)", textDecoration: "none" }}>
          or WhatsApp {desk.name} · {desk.relationship}
        </a>
      )}
    </Mgr>
  );
};

/* ---------- tables ---------- */

export const Tb = ({ head, children, testId, style }) => (
  <div className="tb-wrap">
    <table className="tb" data-testid={testId} style={style}>
      {head && <thead><tr>{head}</tr></thead>}
      <tbody>{children}</tbody>
    </table>
  </div>
);

/* A row that opens its record when tapped anywhere — tick boxes, buttons, links, menus and dialogs inside keep their own job. */
const OWN_JOB = "a, button, input, select, textarea, label, [role='menu'], [role='menuitem'], .menu";
export const openRow = (nav, to) => (e) => {
  const row = e.currentTarget;
  const hit = e.target.closest?.(OWN_JOB);
  if (!row.contains(e.target) || (hit && row.contains(hit)) || window.getSelection?.().toString()) return;
  if (e.metaKey || e.ctrlKey) window.open(to, "_blank", "noopener");
  else nav(to);
};

export const Th = ({ children, className = "", style }) => <th className={className} style={style}>{children}</th>;
export const Td = ({ children, className = "", ...rest }) => <td className={className} {...rest}>{children}</td>;
export const CellMain = ({ children, style, ...rest }) => <div className="cell-main" style={style} {...rest}>{children}</div>;
export const CellSub = ({ children, style, ...rest }) => <div className="cell-sub" style={style} {...rest}>{children}</div>;
export const DateCell = ({ children }) => <div className="date">{children}</div>;

export const Money = ({ v, tone = "ink", size, sub, compact = false, raw, testId }) => {
  const tones = { green: " green", muted: " muted", red: "", attn: " attn" };
  const sizes = { sm: 13.5, md: undefined, lg: 22, xl: 30 };
  return (
    <div style={{ minWidth: 0 }} data-testid={testId}>
      <div className={`money${tones[tone] || ""}`}
           style={{ fontSize: sizes[size], color: tone === "red" ? "var(--red)" : undefined }}>
        {raw ?? gbp(v, { compact })}
      </div>
      {sub && <div className="money-sub">{sub}</div>}
    </div>
  );
};

/* ---------- status ---------- */

const ST = ST_CLASS;
/* A small coloured chip for non-stage labels (partner status, seats, payouts). Case stages use StatusChip. */
export const St = ({ tone = "slate", children, style, testId }) => (
  <span className={`st ${ST[tone] || ST.slate}`} style={style} data-testid={testId}>
    <i className="sq" />{children}
  </span>
);

/* A case stage, everywhere: the coloured box with its square dot. */
export const StatusChip = ({ state, testId, style }) => <StagePill state={state} testId={testId} style={style} />;

export const SubSt = ({ children, testId }) => <div className="sub-st" data-testid={testId}>{children}</div>;

export const Conf = ({ level = "l", children }) => (
  <span className={`conf conf-${level}`}>{children}</span>
);

export const Lname = ({ children, tone }) => (
  <span className={`lname${tone ? ` ${tone}` : ""}`}>{children}</span>
);

export const Badge = ({ children, style }) => <span className="badge" style={style}>{children}</span>;
export const Tag = ({ children, amber }) => <span className={`tag${amber ? " tag-amber" : ""}`}>{children}</span>;
export const Tagk = ({ children }) => <span className="tagk">{children}</span>;

/* ---------- controls ---------- */

export const Btn = ({ kind = "ghost", sm, block, children, as, testId, ...rest }) => {
  const cls = `btn btn-${kind}${sm ? " btn-sm" : ""}${block ? " btn-block" : ""}`;
  const El = as || (rest.href ? "a" : "button");
  return <El className={cls} data-testid={testId} {...rest}>{children}</El>;
};

export const Chips = ({ items, value, onChange, testPrefix }) => (
  <div className="chips">
    {items.map((it) => (
      <button key={it.key} className={`chip${value === it.key ? " on" : ""}`} onClick={() => onChange(it.key)}
              data-testid={`${testPrefix}-${it.key}`}>
        {it.tone && <i className="sq" style={{ background: DOT[it.tone] }} />}
        {it.label}
        {it.count != null && <span className="n">{it.count}</span>}
      </button>
    ))}
  </div>
);
export const FilterChips = Chips;

export const SearchBox = ({ value, onChange, placeholder = "Search", testId }) => (
  <label className="search">
    <I.search />
    <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
           data-testid={testId}
           style={{ border: 0, outline: 0, background: "transparent", font: "inherit", color: "var(--ink)", width: "100%" }} />
  </label>
);

export const Sw = ({ on, onClick, testId, disabled, label }) => (
  <button type="button" role="switch" className={`sw${on ? " on" : ""}`} onClick={onClick} disabled={disabled}
          aria-checked={!!on} aria-label={label} data-testid={testId} />
);

export const Row = ({ title, sub, right, testId }) => (
  <div className="row" data-testid={testId}>
    <div><div className="t">{title}</div>{sub && <div className="s">{sub}</div>}</div>
    {right}
  </div>
);

export const Fld = ({ label, children }) => (
  <div className="fld">{label && <div className="l">{label}</div>}{children}</div>
);

export const Inp = React.forwardRef((props, ref) => <input ref={ref} className="inp" {...props} />);
Inp.displayName = "Inp";

/* ---------- key/value ---------- */

export const Kv = ({ rows, style, left }) => (
  <dl className={`kv${left ? " kv-left" : ""}`} style={style}>
    {rows.filter(Boolean).map(([k, v, extra]) => (
      <React.Fragment key={k}>
        <dt>{k}</dt>
        <dd {...(extra || {})}>{v}</dd>
      </React.Fragment>
    ))}
  </dl>
);

export const KeyValue = ({ label, value, tone }) => (
  <div className="kvrow">
    <span className="k">{label}</span>
    <span className="v" style={tone === "green" ? { color: "var(--green)" } : undefined}>{value}</span>
  </div>
);

export const LinkRow = ({ label, onClick, href, right, testId }) => {
  const El = href ? "a" : "button";
  return (
    <El className="lk" onClick={onClick} href={href} data-testid={testId} type={href ? undefined : "button"}>
      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
      {right || <I.arrow />}
    </El>
  );
};

/* ---------- case ladder and the if-funded range ---------- */

/* The case ladder — five nodes on a track. Done nodes carry a tick (green once funded), the current node is the
   same ring as the stage box (its inner mark fades softly while something is happening), a closed node a cross. */
const Tick = () => <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M2.2 5.2l1.8 1.8 3.8-4.1" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>;
const Cross = () => <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M3 3l4 4M7 3l-4 4" stroke="#64748B" strokeWidth="1.6" strokeLinecap="round" /></svg>;

export const Ladder = ({ steps }) => {
  const funded = steps.every((s) => s.state === "d");
  return (
    <ol className="lad" style={{ "--lad-track": funded ? "#0B6B4F" : "#0055FF" }} data-testid="case-ladder">
      {steps.map((s, i) => {
        const cls = { d: "is-done", n: "is-now", x: "is-x", s: "is-skip" }[s.state] || "";
        const ring = s.ring || "review";
        return (
          <li key={i} className={`lad-s ${cls}${s.live ? " live" : ""}`} style={{ "--i": i, ...(s.state === "n" ? { "--c": ringColor(ring) } : {}) }}
              title={s.state === "s" ? "Not needed on this case" : s.full}
              data-testid={s.state === "n" || s.state === "x" ? "ladder-live-rung" : s.state === "s" ? "ladder-skipped-rung" : undefined}>
            <span className="lad-node">{s.state === "d" ? <Tick /> : s.state === "x" ? <Cross /> : s.state === "n" ? <StageRing kind={ring} live={s.live} size={22} /> : null}</span>
            <span className="lad-label"><span className="lad-short">{s.label}</span><span className="lad-full">{s.full || s.label}</span></span>
          </li>
        );
      })}
    </ol>
  );
};

export const Bar = ({ pct, tone, style }) => (
  <div className="bar" style={style}>
    <i style={{ width: `${Math.max(0, Math.min(100, pct || 0))}%`, background: tone }} />
  </div>
);

/**
 * The if-funded range: low, most-likely and high, with the pin where the money probably lands.
 *
 * The three figures sit in a flex row above the bar rather than absolutely positioned along it.
 * Positioned, "most likely" printed on top of "highest" whenever the likely figure *was* the top
 * of the range — which is the common case once a desk has made an offer.
 */
export const RangeBar = ({ min, likely, max, labels = true, testId }) => {
  const single = !(max > min);
  const span = Math.max(1, (max || 0) - (min || 0));
  const pin = single ? 50 : Math.min(97, Math.max(3, (((likely || 0) - (min || 0)) / span) * 100));
  return (
    <div style={{ margin: labels ? "0" : "18px 0 4px" }} data-testid={testId}>
      {labels && (
        <div className={`range-labs${single ? " one" : ""}`}>
          <span><b>{gbp(min)}</b>{single ? "confirmed" : "lowest"}</span>
          {!single && <span><b>{gbp(likely)}</b>most likely</span>}
          {!single && <span><b>{gbp(max)}</b>highest</span>}
        </div>
      )}
      <div className="range-bar">
        <div className="track" />
        <div className="span" style={{ left: 0, right: single ? 0 : `${Math.max(0, 100 - pin)}%` }} />
        {!single && (
          <div className="pin" style={{ left: `calc(${pin}% - 2px)` }}
               data-testid={testId ? `${testId}-pin` : undefined} />
        )}
      </div>
    </div>
  );
};

export const Opt = ({ title, right, pick, children, style, testId }) => (
  <div className={`opt${pick ? " pick" : ""}`} style={style} data-testid={testId}>
    {(title || right) && <div className="h"><div className="t">{title}</div>{right}</div>}
    {children}
  </div>
);

/* ---------- desks, masked ---------- */

export const DeskAlias = ({ alias, kind, index = 0, testId }) => {
  const shade = ["", "b", "c", "d", "e"][index % 5];
  return (
    <div className="alias" data-testid={testId}>
      <div className={`av${shade ? ` ${shade}` : ""}`}>{(alias || "Desk A").split(" ").pop()}</div>
      <div style={{ minWidth: 0 }}>
        <div className="nm">{alias}</div>
        {kind && <div className="kind">{kind}</div>}
      </div>
    </div>
  );
};

/* ---------- notes ---------- */

export const NoteCard = ({ who, tag, tagAmber, at, mine, children, actions, testId }) => (
  <div className={`note${mine ? " mine" : ""}`} data-testid={testId}>
    <div className="note-h">
      <div style={{ display: "flex", alignItems: "center", gap: 11, flexWrap: "wrap" }}>
        <span className="who-s">{who}</span>
        {tag && <Tag amber={tagAmber}>{tag}</Tag>}
      </div>
      <span className="date">{at}</span>
    </div>
    <div style={{ color: "var(--ink-3)", fontSize: 13.5, whiteSpace: "pre-wrap" }}>{children}</div>
    {actions && <div style={{ display: "flex", gap: 9, marginTop: 14, flexWrap: "wrap" }}>{actions}</div>}
  </div>
);

export const Note = ({ children, testId }) => (
  <p className="note" data-testid={testId}
     style={{ background: "var(--accent-soft)", borderColor: "rgba(125,169,255,.5)", color: "var(--ink-3)", fontSize: 12.5, margin: 0, lineHeight: 1.6 }}>
    {children}
  </p>
);

export const Warn = ({ children, testId, style }) => <div className="warn" data-testid={testId} style={style}>{children}</div>;

export const Empty = ({ children, testId }) => <div className="empty" data-testid={testId}>{children}</div>;

/* ---------- tabs ---------- */

export const Tabs = ({ items, value, onChange, testPrefix }) => (
  <div className="tabs" role="tablist">
    {items.map((t) => (
      <a key={t.key} href="#" role="tab" aria-selected={value === t.key}
         data-state={value === t.key ? "active" : "inactive"}
         className={value === t.key ? "on" : undefined}
         onClick={(e) => { e.preventDefault(); onChange(t.key); }}
         data-testid={testPrefix ? `${testPrefix}-${t.key}` : undefined}>
        {t.label}
        {t.count != null && <span className="num" style={{ color: "var(--mut)", marginLeft: 5 }}>{t.count}</span>}
      </a>
    ))}
  </div>
);

/* ---------- copy to clipboard ---------- */

export const CopyField = ({ value, label, testId }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value || "");
      setDone(true);
      toast.success("Copied");
      setTimeout(() => setDone(false), 1600);
    } catch { toast.error("Copy failed — select the text instead"); }
  };
  return (
    <div data-testid={testId}>
      {label && <div className="rail-t" style={{ marginBottom: 8 }}>{label}</div>}
      <div style={{ display: "flex", gap: 9, alignItems: "center" }}>
        <div className="copyrow" style={{ flex: 1, minWidth: 0 }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {(value || "").replace(/^https?:\/\//, "")}
          </span>
        </div>
        <Btn sm onClick={copy} testId={testId ? `${testId}-copy` : undefined}>{done ? "Copied" : "Copy"}</Btn>
      </div>
    </div>
  );
};

/* ---------- a confirmation that does not argue with the page ---------- */

export const useFlash = () => {
  const [msg, setMsg] = useState(null);
  const t = useRef();
  useEffect(() => () => clearTimeout(t.current), []);
  return [
    msg,
    (m) => { setMsg(m); clearTimeout(t.current); t.current = setTimeout(() => setMsg(null), 3200); },
  ];
};

export const Flash = ({ children, testId }) =>
  children ? <div className="cx-toast" data-testid={testId}><i className="sq" style={{ background: "var(--green)", width: 9, height: 9, borderRadius: 3 }} />{children}</div> : null;
